/**
 * Adapter: konwersja zaawansowanych filtrów WiseTable na parametry query dla Symfony.
 * Backend zawsze oczekuje parametru filter.
 *
 * Semantyka:
 * - AND łączy reguły filtrujące (filtry kolumn). Każda reguła = warunki dla jednej kolumny.
 * - OR łączy warunki wewnątrz reguły (np. status eq 0 OR status eq 1).
 *
 * Struktura: filter[and][ruleIndex][or][conditionIndex][field/op/value]
 * Np. (status eq 0 OR status eq 1) → filter[and][0][or][0]=..., filter[and][0][or][1]=...
 *     (status eq 0 OR status eq 1) AND (name contains x) → filter[and][0][or][0/1]=..., filter[and][1][or][0]=...
 *
 * filtersConfig – opcjonalna konfiguracja filtrów per kolumna; jeśli filtr ma query_field_symbol,
 * w query wysyłane jest query_field_symbol zamiast columnFieldSymbol (np. client_name → client_id).
 */

import type {
  IWiseTableAdvancedFilter,
  IWiseTableFilter,
  TWiseTableAdvancedFilterOperator
} from 'components/wise/WiseTable/types';

const COMPARISON_OPERATORS: TWiseTableAdvancedFilterOperator[] = [
  'eq',
  'gt',
  'lt',
  'contains'
];

function isComparisonOperator(
  op: TWiseTableAdvancedFilterOperator
): op is 'eq' | 'gt' | 'lt' | 'contains' {
  return COMPARISON_OPERATORS.includes(op);
}

/**
 * Dzieli tablicę filtrów na reguły (segmenty) rozdzielone operatorem "and".
 * Każda reguła = jeden filtr kolumny (może mieć wiele warunków łączonych OR wewnątrz).
 * Np. [ status eq 0, or, status eq 1 ] → jedna reguła [ status eq 0, status eq 1 ]
 *     [ status eq 0, and, name contains meble ] → dwie reguły
 */
function splitByAndConnector(
  filters: IWiseTableAdvancedFilter[]
): IWiseTableAdvancedFilter[][] {
  const segments: IWiseTableAdvancedFilter[][] = [];
  let current: IWiseTableAdvancedFilter[] = [];

  for (const f of filters) {
    if (f.operator === 'and' && !f.columnFieldSymbol) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    if (f.operator === 'or' && !f.columnFieldSymbol) {
      continue;
    }
    if (f.columnFieldSymbol && isComparisonOperator(f.operator)) {
      current.push(f);
    }
  }

  if (current.length > 0) {
    segments.push(current);
  }

  return segments;
}

/**
 * Zwraca nazwę pola do query: z filtersConfig (query_field_symbol) lub columnFieldSymbol.
 */
function resolveQueryField(
  columnFieldSymbol: string,
  filtersConfig?: IWiseTableFilter[]
): string {
  if (filtersConfig?.length) {
    const config = filtersConfig.find((c) => c.field_symbol === columnFieldSymbol);
    if (config?.query_field_symbol) return config.query_field_symbol;
  }
  return columnFieldSymbol;
}

/**
 * Zamienia listę filtrów na parametry query.
 * AND między regułami; wewnątrz reguły warunki w kluczu [or] (nawet gdy jeden).
 * filtersConfig – opcjonalna mapa kolumna → query_field_symbol (np. client_name → client_id).
 */
export function advancedFiltersToParams(
  filters: IWiseTableAdvancedFilter[],
  filtersConfig?: IWiseTableFilter[]
): Record<string, string> {
  const rules = splitByAndConnector(filters);
  const rulesConditions = rules
    .map((segment) =>
      segment
        .filter(
          (f) =>
            f.columnFieldSymbol &&
            isComparisonOperator(f.operator) &&
            f.value != null
        )
        .map((f) => ({
          field: resolveQueryField(f.columnFieldSymbol!, filtersConfig),
          op: f.operator!,
          value: String(f.value).trim()
        }))
    )
    .filter((arr) => arr.length > 0);

  if (rulesConditions.length === 0) {
    return {};
  }

  const flat: Record<string, string> = {};
  rulesConditions.forEach((conditions, ruleIndex) => {
    conditions.forEach((condition, conditionIndex) => {
      const prefix = `filter[and][${ruleIndex}][or][${conditionIndex}]`;
      flat[`${prefix}[field]`] = condition.field;
      flat[`${prefix}[op]`] = condition.op;
      flat[`${prefix}[value]`] = condition.value;
    });
  });
  return flat;
}
